import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpider, faChartBar, faSync, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { getActiveCrawls } from '../services/api';
import { Link } from 'react-router-dom';
import { getActiveCrawlJobs } from '../services/crawlStatusService';
import './CrawlStatusIcon.css';

// Global variable to track if crawling was initiated in this session
let crawlingInitiated = false;

// Function to set crawling as initiated (to be called from UploadForm)
export const setCrawlingInitiated = (initiated: boolean = true) => {
  crawlingInitiated = initiated;
};

const CrawlStatusIcon: React.FC = () => {
  const [activeCrawls, setActiveCrawls] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Check for active crawls only when crawling has been initiated
  useEffect(() => {
    if (!crawlingInitiated) return;

    const checkActiveCrawls = async () => {
      try {
        setLoading(true);
        const result = await getActiveCrawls();
        if (result && result.active_crawls) {
          setActiveCrawls(result.active_crawls);
          
          // If there's an active crawl and none selected yet, select the first one
          if (result.active_crawls.length > 0 && !selectedClientId) {
            setSelectedClientId(result.active_crawls[0]);
          }
          
          // Reset crawlingInitiated if no active crawls found
          if (result.active_crawls.length === 0) {
            crawlingInitiated = false;
          }
        }
      } catch (error) {
        console.error('Error fetching active crawls:', error);
      } finally {
        setLoading(false);
      }
    };

    // Check immediately
    checkActiveCrawls();

    // Then set up interval to check every 10 seconds if icon is visible
    // but only if crawling was initiated
    const interval = setInterval(() => {
      // Only check if crawling was initiated or we know there are active crawls
      if (crawlingInitiated || activeCrawls.length > 0) {
        checkActiveCrawls();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedClientId]);

  // Don't show anything if there are no active crawls
  if (activeCrawls.length === 0) {
    return null;
  }

  // Don't show status icon if PersistentCrawlMonitor is already displaying crawl widgets
  const localCrawlJobs = getActiveCrawlJobs().filter(job => !job.hidden);
  if (localCrawlJobs.length > 0) {
    // PersistentCrawlMonitor has this covered - don't show duplicate
    return null;
  }

  // Instead of opening a monitor, we'll link to the crawl details page
  return (
    <div className="crawl-status-icon-container">
      <Link
        to={`/crawl-details/${selectedClientId || activeCrawls[0]}`}
        className="crawl-status-icon"
        title={`${activeCrawls.length} active crawling job${activeCrawls.length !== 1 ? 's' : ''} - Click to view details`}
      >
        {loading ? (
          <FontAwesomeIcon icon={faSync} spin />
        ) : (
          <>
            <FontAwesomeIcon icon={faSpider} />
            {activeCrawls.length > 1 && (
              <span className="crawl-count">{activeCrawls.length}</span>
            )}
            <FontAwesomeIcon icon={faExternalLinkAlt} className="icon-sm" />
          </>
        )}
      </Link>
    </div>
  );
};

export default CrawlStatusIcon; 